// src/hooks/useDocumentClassification.ts

import { useState, useCallback, useEffect } from "react";
import { ClassificationResponse, AgentType, ContractTypeInfo } from "../interfaces/classification";
import {
  classifyDocument,
  createXmlFileForClassification,
  mapAgentTypeToInternalId,
  ClassificationApiError,
} from "../api/classificationApi";
import { createLogger } from "../utils/logger";
import { track } from "../utils/analytics";
import { useAuth } from "../contexts/AuthContext";
import { useChatSettings } from "../contexts/ChatSettingsContext";
import { nullToUndefined } from "../utils/typeHelpers";
import { modifyJwtTenantId } from "../utils/jwtUtils";
import { useTenantSelectorAccess } from "./useTenantSelectorAccess";

const log = createLogger("DocumentClassification");

/**
 * Classification state interface
 */
interface ClassificationState {
  isClassifying: boolean;
  classificationResult: ClassificationResponse | null;
  recommendedAgentId: string | null;
  contractTypeInfo: ContractTypeInfo | null;
  error: string | null;
  hasClassified: boolean;
}

/**
 * Classification result with both agent ID and contract type
 */
interface ClassificationResult {
  agentId: string | null;
  contractType: string | null;
}

/**
 * Hook return type with all necessary methods and state
 */
interface UseDocumentClassificationReturn extends ClassificationState {
  classifyDocumentContent: (ooxmlContent: string) => Promise<ClassificationResult>;
  resetClassification: () => void;
  getRecommendedAgentId: () => string | null;
  getContractType: () => string | null;
  getContractTypeInfo: () => ContractTypeInfo | null;
}

/**
 * Custom hook for document classification
 *
 * This hook manages the document classification process, including:
 * - Calling the classification API
 * - Managing loading and error states
 * - Mapping agent types to internal IDs
 * - Caching classification results for the session
 */
export function useDocumentClassification(): UseDocumentClassificationReturn {
  const { accessToken } = useAuth();
  const { getTenantId } = useChatSettings();
  const tenantSelectorAllowed = useTenantSelectorAccess();
  const [state, setState] = useState<ClassificationState>({
    isClassifying: false,
    classificationResult: null,
    recommendedAgentId: null,
    contractTypeInfo: null,
    error: null,
    hasClassified: false,
  });

  /**
   * Classify document content and return both agent ID and contract type
   *
   * @param ooxmlContent The OOXML content from the Word document
   * @returns Object containing both agentId and contractType
   */
  const classifyDocumentContent = useCallback(
    async (ooxmlContent: string): Promise<ClassificationResult> => {
      log.debug("📋 CLASSIFY CONTENT CALLED:", {
        contentLength: ooxmlContent.length,
        hasClassified: state.hasClassified,
        recommendedAgentId: state.recommendedAgentId,
      });
      // Don't re-classify if we already have a result (unless explicitly reset)
      if (state.hasClassified && state.recommendedAgentId) {
        log.info("Using cached classification result", {
          cachedAgentId: state.recommendedAgentId,
          cachedContractType: state.contractTypeInfo?.contractType,
        });
        return {
          agentId: state.recommendedAgentId,
          contractType: state.contractTypeInfo?.contractType || null,
        };
      }

      log.info("Starting document classification", {
        contentLength: ooxmlContent.length,
      });

      setState((prev) => ({
        ...prev,
        isClassifying: true,
        error: null,
      }));

      try {
        // Create file from OOXML content
        const file = createXmlFileForClassification(ooxmlContent, `document_${Date.now()}.xml`);

        // Modify access token with selected tenant ID when tenant selector is enabled
        let tokenToUse = accessToken;
        const tenantId = getTenantId();
        if (tenantSelectorAllowed && tenantId && accessToken) {
          tokenToUse = modifyJwtTenantId(accessToken, tenantId);
          log.info("JWT token modified with tenant ID for classification", { tenantId });
        }

        // Call classification API
        const response = await classifyDocument(
          file,
          {
            // You can add session ID or other params here if needed
            // id: sessionId,
            bypass_cache: false,
          },
          nullToUndefined(tokenToUse)
        );

        // Map the agent type to internal ID
        const recommendedAgentId = mapAgentTypeToInternalId(
          response.data.agent_recommendation.agent_type
        );

        // Extract contract type information
        const contractTypeInfo: ContractTypeInfo = {
          contractType: response.data.document_type.type,
          confidence: response.data.document_type.confidence_score,
          source: "classification",
        };

        log.info("Classification completed successfully", {
          documentType: response.data.document_type.type,
          agentType: response.data.agent_recommendation.agent_type,
          recommendedAgentId,
          confidence: response.data.agent_recommendation.confidence_score,
          contractType: contractTypeInfo.contractType,
        });

        setState({
          isClassifying: false,
          classificationResult: response,
          recommendedAgentId,
          contractTypeInfo,
          error: null,
          hasClassified: true,
        });

        track("app:document_classified", {
          contract_type: contractTypeInfo.contractType,
          confidence: contractTypeInfo.confidence,
          recommended_agent_id: recommendedAgentId,
        });

        return {
          agentId: recommendedAgentId,
          contractType: contractTypeInfo.contractType,
        };
      } catch (error) {
        // Use backend error message directly
        const errorMessage = (error as any)?.message || "Failed to classify document";

        track("app:document_classification_failed", {
          error_code: error instanceof ClassificationApiError ? error.errorCode : undefined,
          error_message: errorMessage,
        });

        if (error instanceof ClassificationApiError) {
          log.error("Classification API error", {
            errorCode: error.errorCode,
            statusCode: error.statusCode,
            requestId: error.requestId,
            details: error.details,
          });
        } else {
          log.error("Unexpected classification error", {
            error: error instanceof Error ? error.message : String(error),
          });
        }

        // Default to red-line-analyzer on error
        const defaultAgentId = "red-line-analyzer";

        setState({
          isClassifying: false,
          classificationResult: null,
          recommendedAgentId: defaultAgentId,
          contractTypeInfo: {
            contractType: "Unknown",
            confidence: 0,
            source: "unknown",
          },
          error: errorMessage,
          hasClassified: true,
        });

        // Return default agent ID and unknown contract type on error
        return {
          agentId: defaultAgentId,
          contractType: "Unknown",
        };
      }
    },
    [state.hasClassified, state.recommendedAgentId, accessToken, getTenantId, tenantSelectorAllowed]
  );

  /**
   * Reset classification state
   * Use this when document changes or user wants to re-classify
   */
  const resetClassification = useCallback(() => {
    log.info("Resetting classification state");

    setState({
      isClassifying: false,
      classificationResult: null,
      recommendedAgentId: null,
      contractTypeInfo: null,
      error: null,
      hasClassified: false,
    });
  }, []);

  /**
   * Get the current recommended agent ID
   * Returns null if no classification has been performed
   */
  const getRecommendedAgentId = useCallback(() => {
    return state.recommendedAgentId;
  }, [state.recommendedAgentId]);

  /**
   * Get the contract type string
   * Returns null if no classification has been performed
   */
  const getContractType = useCallback(() => {
    return state.contractTypeInfo?.contractType || null;
  }, [state.contractTypeInfo]);

  /**
   * Get the complete contract type information
   * Returns null if no classification has been performed
   */
  const getContractTypeInfo = useCallback(() => {
    return state.contractTypeInfo;
  }, [state.contractTypeInfo]);

  // Log state changes for debugging
  useEffect(() => {
    log.debug("Classification state updated", {
      isClassifying: state.isClassifying,
      hasResult: !!state.classificationResult,
      recommendedAgentId: state.recommendedAgentId,
      contractType: state.contractTypeInfo?.contractType,
      hasError: !!state.error,
      hasClassified: state.hasClassified,
    });
  }, [state]);

  return {
    ...state,
    classifyDocumentContent,
    resetClassification,
    getRecommendedAgentId,
    getContractType,
    getContractTypeInfo,
  };
}
